<script lang="ts">
  import { Link } from 'svelte-routing';
  import { api } from '../../api/client';
  import type { Activity, Companion, Member } from '../../api/types';
  import { groups } from '../../stores/groups';
  import { confirm } from '../../stores/confirmDialog';
  import Avatar from '../Avatar.svelte';
  import { locale, t } from '../../i18n/index.ts';

  // Companions lists who an activity was done with (spec 006 FR-074): a
  // matched member opens their own activity. The owner adds a member they
  // share a group with, which names them here alone, or removes someone,
  // which takes a match off both activities for good (006 FR-075).
  let { activity, own, onchange }: { activity: Activity; own: boolean; onchange: (a: Activity) => void } = $props();

  let adding = $state(false);
  let people = $state<Member[]>([]);
  let pick = $state('');
  let error = $state('');

  let list = $derived(activity.companions ?? []);

  function names(cs: Companion[]): string {
    return new Intl.ListFormat(locale(), { type: 'conjunction' }).format(cs.map((c) => c.display_name));
  }

  async function startAdding() {
    adding = true;
    try {
      const gs = await groups.load();
      const seen = new Map<string, Member>();
      for (const g of gs) {
        for (const m of await api.get<Member[]>(`/groups/${g.id}/members`)) seen.set(m.user_id, m);
      }
      people = [...seen.values()]
        .filter((m) => m.user_id !== activity.user_id && !list.some((c) => c.user_id === m.user_id))
        .sort((a, b) => a.display_name.localeCompare(b.display_name));
      pick = people[0]?.user_id ?? '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function add() {
    if (!pick) return;
    try {
      const got = await api.post<Activity>(`/me/activities/${activity.id}/companions`, { user_id: pick });
      onchange({ ...activity, companions: got.companions ?? [] });
      adding = false;
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function remove(c: Companion) {
    const ask = c.activity_id
      ? t('activitiesCompanionsRemoveConfirmMatched', { member: c.display_name })
      : t('activitiesCompanionsRemoveConfirm', { member: c.display_name });
    if (!(await confirm(ask))) return;
    try {
      await api.delete(`/me/activities/${activity.id}/companions/${c.user_id}`);
      onchange({ ...activity, companions: list.filter((x) => x.user_id !== c.user_id) });
    } catch (e: any) {
      error = e.message;
    }
  }
</script>

{#if list.length > 0 || (own && activity.state === 'finished')}
  <section class="space-y-2" aria-labelledby="together-heading">
    <h2 id="together-heading" class="font-medium">{list.length ? t('activitiesCompanionsWith', { names: names(list) }) : t('activitiesCompanionsTogether')}</h2>
    {#if list.length}
      <ul class="flex flex-wrap gap-3">
        {#each list as c (c.user_id)}
          <li class="flex items-center gap-2">
            {#if c.activity_id}
              <Link to={c.group_id ? `/activity/${c.group_id}/${c.activity_id}` : `/activity/${c.activity_id}`} class="flex items-center gap-2 hover:opacity-80">
                <Avatar name={c.display_name} userId={c.user_id} color="var(--theme-overlay2)" photo={c.photo} size={32} />
                <span class="text-sm">{c.display_name}</span>
              </Link>
            {:else}
              <Avatar name={c.display_name} userId={c.user_id} color="var(--theme-overlay2)" photo={c.photo} size={32} />
              <span class="text-sm">{c.display_name}</span>
            {/if}
            {#if own}
              <button class="btn btn-secondary btn-sm" aria-label={t('activitiesCompanionsRemove', { member: c.display_name })} onclick={() => remove(c)}><i aria-hidden="true" class="las la-times"></i></button>
            {/if}
          </li>
        {/each}
      </ul>
    {/if}
    {#if own && activity.state === 'finished'}
      {#if adding}
        <div class="flex flex-wrap items-center gap-2 text-sm">
          {#if people.length}
            <select class="input py-1" bind:value={pick} aria-label={t('activitiesCompanionsWho')}>
              {#each people as p (p.user_id)}
                <option value={p.user_id}>{p.display_name}</option>
              {/each}
            </select>
            <button class="btn btn-primary btn-sm" onclick={add}>{t('appAdd')}</button>
          {:else}
            <span class="text-text-muted">{t('activitiesCompanionsAllListed')}</span>
          {/if}
          <button class="btn btn-secondary btn-sm" onclick={() => (adding = false)}>{t('appCancel')}</button>
        </div>
      {:else}
        <button class="btn btn-secondary btn-sm" onclick={startAdding}><i aria-hidden="true" class="las la-user-friends mr-1"></i>{t('activitiesCompanionsAdd')}</button>
      {/if}
      <p class="text-xs text-text-muted">{t('activitiesCompanionsNote')}</p>
    {/if}
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {/if}
  </section>
{/if}
